import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  HiOutlineUsers,
  HiOutlineUserGroup,
  HiOutlineMail,
  HiOutlineSearch,
  HiOutlineDownload,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlinePlus,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineClock,
  HiOutlinePhone,
  HiOutlineOfficeBuilding,
  HiOutlineLockClosed,
  HiOutlineLockOpen,
  HiOutlineKey,
  HiOutlineShieldCheck,
  HiOutlineShieldExclamation,
  HiOutlineColorSwatch,
  HiOutlineUserAdd,
  HiOutlineUserRemove,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineCalendar,
  HiOutlineStar,
  HiOutlineIdentification,
  HiOutlineFilter,
  HiOutlineDotsVertical
} from 'react-icons/hi';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { useModal } from '../hooks/useModal';
import { useToast } from '../hooks/useToast';
import type {
  UserRecord,
  UserGroupRecord,
  GroupMember,
  UserFilters,
  UserGroupFilters
} from '../types';
import {
  getUsers,
  getGroups,
  getGroupMembers,
  createGroup,
  updateGroup,
  deleteGroup,
  addUserToGroup,
  removeUserFromGroup,
  createUser,
  updateUser,
  deleteUser,
  exportUsersToExcel,
  blockUser,
  unblockUser,
  bulkBlockUsers,
  bulkUnblockUsers,
  resetUserPassword
} from '../services/usersApi';


type TabType = 'usuarios' | 'grupos';
type UserGroup = UserGroupRecord;

const PUESTOS = ['Operador', 'Comisionista', 'Asesor'];
const SOLICITUD_ESTADOS = ['Aprobada', 'Pendiente', 'Rechazada'];
const GROUP_COLORS = [
  '#652d86', '#7ab900', '#00d4ff', '#ff6b9d', '#ff9f43',
  '#5f7cff', '#20e3b2', '#9ca3af', '#f43f5e', '#a855f7',
  '#06b6d4', '#84cc16', '#ef4444', '#f59e0b', '#8b5cf6'
];
const PROTECTED_GROUPS = ['Appy-Admin', 'Appy-User'];
const ADMIN_GROUP = 'Appy-Admin';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}


interface PasswordRule {
  key: string;
  label: string;
  test: (password: string) => boolean;
}

const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', label: 'Al menos 14 caracteres', test: (p) => p.length >= 14 },
  { key: 'uppercase', label: 'Al menos una mayúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'Al menos una minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Al menos un número', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'Al menos un carácter especial', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(p) },
];

function usePasswordStrength(password: string) {
  return useMemo(() => {
    const results = PASSWORD_RULES.map((rule) => ({
      ...rule,
      passed: rule.test(password),
    }));
    const passedCount = results.filter((r) => r.passed).length;
    const total = results.length;
    const allPassed = passedCount === total;

    let level: 'empty' | 'weak' | 'fair' | 'good' | 'strong' = 'empty';
    let color = 'var(--text-muted)';
    let label = '';

    if (password.length === 0) {
      level = 'empty';
    } else if (passedCount <= 2) {
      level = 'weak';
      color = '#ef4444';
      label = 'Débil';
    } else if (passedCount <= 3) {
      level = 'fair';
      color = '#f59e0b';
      label = 'Regular';
    } else if (passedCount <= 4) {
      level = 'good';
      color = '#7ab900';
      label = 'Buena';
    } else {
      level = 'strong';
      color = '#22c55e';
      label = 'Fuerte';
    }

    return { results, passedCount, total, allPassed, level, color, label };
  }, [password]);
}


function PasswordStrengthIndicator({ password }: { password: string }) {
  const { results, passedCount, total, level, color, label } = usePasswordStrength(password);

  if (password.length === 0) return null;

  return (
    <div style={{ marginTop: '8px' }}>
     
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: i < passedCount ? color : 'var(--border-color)',
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>

     
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>
          {label}
        </span>
        <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
          {passedCount}/{total} requisitos
        </span>
      </div>

     
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        padding: '10px 12px',
        background: 'var(--bg-body)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-light)',
      }}>
        {results.map((rule) => (
          <div
            key={rule.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.75rem',
              color: rule.passed ? '#22c55e' : 'var(--text-muted)',
              transition: 'color 0.2s ease',
            }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: rule.passed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(156, 163, 175, 0.12)',
              flexShrink: 0,
              transition: 'background 0.2s ease',
            }}>
              {rule.passed ? (
                <HiOutlineCheck style={{ fontSize: '0.625rem' }} />
              ) : (
                <HiOutlineX style={{ fontSize: '0.625rem' }} />
              )}
            </span>
            {rule.label}
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * Modal de carga bloqueante para la exportación de usuarios.
 * No tiene botón de cierre, no tiene onClose, y no escucha Escape.
 * Solo desaparece cuando el estado `exporting` del padre cambia a `false`
 * (es decir, cuando la descarga termina, ya sea con éxito o con error).
 */
function ExportLoadingModal({ fileName }: { fileName?: string }) {
  const isMobile = useIsMobile();

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="card"
        style={{
          width: isMobile ? '90%' : '380px',
          borderRadius: 'var(--radius-lg)',
          padding: '2rem 1.75rem',
          textAlign: 'center',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(101, 45, 134, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem',
            position: 'relative',
          }}
        >
          <div
            className="spinner"
            style={{
              position: 'absolute',
              width: '64px',
              height: '64px',
              borderWidth: '3px',
            }}
          />
          <HiOutlineDownload style={{ fontSize: '1.5rem', color: 'var(--primary)' }} />
        </div>

        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.0625rem', fontWeight: 600 }}>
          Preparando tu descarga
        </h3>
        <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Estamos generando{fileName ? ` "${fileName}"` : ' el archivo'}. Esto puede tardar unos
          segundos dependiendo de la cantidad de usuarios.
        </p>

        <div
          style={{
            marginTop: '1.25rem',
            padding: '10px 14px',
            background: 'rgba(255, 159, 67, 0.1)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.75rem',
            color: 'var(--accent-orange)',
            fontWeight: 500,
            textAlign: 'left',
          }}
        >
          <HiOutlineClock style={{ fontSize: '1rem', flexShrink: 0 }} />
          No cierres ni recargues esta ventana hasta que finalice.
        </div>
      </div>
    </div>
  );
}


function UsersPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TabType>('usuarios');
  const isMobile = useIsMobile();

  const tabs: { id: TabType; label: string; icon: React.ComponentType<{ style?: React.CSSProperties }> }[] = [
    { id: 'usuarios', label: 'Usuarios', icon: HiOutlineUsers },
    { id: 'grupos', label: 'Grupos', icon: HiOutlineUserGroup }
  ];

  return (
    <div className="users-page fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="page-header" style={{ marginBottom: '0.75rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h1 className="page-title" style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', margin: 0, lineHeight: 1.2 }}>
            Gestión de Usuarios
          </h1>
          {!isMobile && <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '2px 0 0' }}>Administra usuarios y grupos del sistema</p>}
        </div>
      </div>

      <div className="tabs-container" style={{ marginBottom: '0.75rem', flexShrink: 0 }}>
        <div className="tabs" style={{ display: 'flex', gap: '4px', background: 'var(--bg-body)', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-color)', width: isMobile ? '100%' : 'fit-content' }}>
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '12px 16px' : '10px 20px', border: 'none', borderRadius: 'var(--radius-md)', background: activeTab === tab.id ? 'var(--bg-card)' : 'transparent', color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: activeTab === tab.id ? 600 : 500, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none', flex: isMobile ? 1 : 'unset' }}>
              <tab.icon style={{ fontSize: '1.125rem' }} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {activeTab === 'usuarios' && <UsersTab />}
        {activeTab === 'grupos' && <GroupsTab />}
      </div>
    </div>
  );
}

function UsersTab(): JSX.Element {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<UserFilters>({ search: '', userRole: '', enabled: undefined });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);

  const isMobile = useIsMobile();
  const { modalState, showSuccess, showError, showConfirm, showDelete, closeModal, setLoading: setModalLoading } = useModal();
  const { toastState, showSuccess: toastSuccess, showError: toastError, closeToast } = useToast();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUsers(page, 10, filters);
      setUsers(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      toastError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { void fetchUsers(); }, [fetchUsers]);
  useEffect(() => {
    const handleClickOutside = () => setActiveActionMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1); };
  const handlePuestoFilter = (e: React.ChangeEvent<HTMLSelectElement>) => { setFilters(prev => ({ ...prev, userRole: e.target.value })); setPage(1); };
  const handleEstadoFilter = (e: React.ChangeEvent<HTMLSelectElement>) => { const value = e.target.value; setFilters(prev => ({ ...prev, enabled: value === '' ? undefined : value === 'true' })); setPage(1); };
  const handleClearFilters = () => { setFilters({ search: '', userRole: '', enabled: undefined }); setPage(1); };
  const hasActiveFilters = filters.search || filters.userRole || filters.enabled !== undefined;

  const handleSelectUser = (userId: string) => { setSelectedUsers(prev => { const newSet = new Set(prev); if (newSet.has(userId)) newSet.delete(userId); else newSet.add(userId); return newSet; }); };
  const handleSelectAll = () => { if (selectedUsers.size === users.length) setSelectedUsers(new Set()); else setSelectedUsers(new Set(users.map(u => u.id))); };

  const handleBulkBlock = async () => {
    if (selectedUsers.size === 0) return;
    setBulkActionLoading(true);
    try {
      await bulkBlockUsers(Array.from(selectedUsers));
      toastSuccess({ title: '¡Éxito!', message: `${selectedUsers.size} usuarios bloqueados` });
      setSelectedUsers(new Set());
      void fetchUsers();
    } catch {
      toastError('Error al bloquear usuarios');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkUnblock = async () => {
    if (selectedUsers.size === 0) return;
    setBulkActionLoading(true);
    try {
      await bulkUnblockUsers(Array.from(selectedUsers));
      toastSuccess({ title: '¡Éxito!', message: `${selectedUsers.size} usuarios desbloqueados` });
      setSelectedUsers(new Set());
      void fetchUsers();
    } catch {
      toastError('Error al desbloquear usuarios');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedUsers.size === 0) return;
    showDelete({
      title: '¿Eliminar usuarios?',
      message: `¿Estás seguro de eliminar ${selectedUsers.size} usuario(s)? Esta acción no se puede deshacer.`,
      confirmText: 'Sí, eliminar',
      onConfirm: async () => {
        setModalLoading(true);
        try {
          await Promise.all(Array.from(selectedUsers).map(id => deleteUser(id)));
          closeModal();
          toastSuccess({ title: '¡Eliminados!', message: `${selectedUsers.size} usuarios eliminados` });
          setSelectedUsers(new Set());
          void fetchUsers();
        } catch {
          closeModal();
          showError({ title: 'Error', message: 'No se pudieron eliminar algunos usuarios' });
        }
      }
    });
  };

  const handleDelete = (user: UserRecord) => {
    showDelete({
      title: '¿Eliminar usuario?',
      message: `¿Estás seguro de eliminar a ${user.firstName} ${user.lastName}?`,
      confirmText: 'Sí, eliminar',
      onConfirm: async () => {
        setModalLoading(true);
        try {
          await deleteUser(user.id);
          closeModal();
          toastSuccess({ title: '¡Eliminado!', message: 'Usuario eliminado' });
          void fetchUsers();
        } catch {
          closeModal();
          showError({ title: 'Error', message: 'No se pudo eliminar' });
        }
      }
    });
  };

  const handleBlock = (user: UserRecord) => {
    const isBlocking = user.enabled;
    showConfirm({
      title: `¿${isBlocking ? 'Bloquear' : 'Desbloquear'} usuario?`,
      message: `¿Estás seguro de ${isBlocking ? 'bloquear' : 'desbloquear'} a ${user.firstName}?`,
      confirmText: isBlocking ? 'Bloquear' : 'Desbloquear',
      onConfirm: async () => {
        setModalLoading(true);
        try {
          if (isBlocking) await blockUser(user.id);
          else await unblockUser(user.id);
          closeModal();
          toastSuccess({ title: isBlocking ? 'Bloqueado' : 'Desbloqueado', message: `${user.firstName} ${isBlocking ? 'bloqueado' : 'desbloqueado'}` });
          void fetchUsers();
        } catch {
          closeModal();
          showError({ title: 'Error', message: 'No se pudo completar' });
        }
      }
    });
  };

  const handleEditUser = (user: UserRecord) => { setEditingUser(user); setShowUserModal(true); setActiveActionMenu(null); };
  const handleNewUser = () => { setEditingUser(null); setShowUserModal(true); };
  const handleUserModalSuccess = (message: string) => { setShowUserModal(false); setEditingUser(null); toastSuccess({ title: '¡Éxito!', message }); void fetchUsers(); };
  const handleUserModalError = (message: string) => { showError({ title: 'Error', message }); };
  const handleOpenPasswordModal = (userId: string) => { setSelectedUserId(userId); setShowPasswordModal(true); setActiveActionMenu(null); };
  const handlePasswordModalSuccess = () => { setShowPasswordModal(false); setSelectedUserId(null); toastSuccess({ title: '¡Éxito!', message: 'Contraseña reseteada' }); };
  const handlePasswordModalError = (message: string) => { showError({ title: 'Error', message }); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportUsersToExcel(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'usuarios_export.csv';
      a.click();
      URL.revokeObjectURL(url);
      toastSuccess({ title: '¡Éxito!', message: 'Archivo exportado' });
    } catch {
      toastError('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const getSolicitudBadge = (solicitud: string) => {
    const styles: Record<string, { bg: string; color: string; icon: JSX.Element }> = {
      'Aprobada': { bg: 'rgba(122, 185, 0, 0.12)', color: 'var(--secondary)', icon: <HiOutlineCheck /> },
      'Pendiente': { bg: 'rgba(255, 159, 67, 0.12)', color: 'var(--accent-orange)', icon: <HiOutlineClock /> },
      'Rechazada': { bg: 'rgba(255, 107, 157, 0.12)', color: 'var(--accent-pink)', icon: <HiOutlineX /> }
    };
    const style = styles[solicitud] ?? styles['Pendiente'];
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600, background: style?.bg, color: style?.color, whiteSpace: 'nowrap' }}>{style?.icon}{solicitud}</span>;
  };

  const getEstadoBadge = (enabled: boolean) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 600, background: enabled ? 'rgba(122, 185, 0, 0.12)' : 'rgba(156, 163, 175, 0.12)', color: enabled ? 'var(--secondary)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
      {enabled ? <HiOutlineShieldCheck /> : <HiOutlineShieldExclamation />}
      {enabled ? 'Activo' : 'Inactivo'}
    </span>
  );

  const renderMobileCard = (user: UserRecord) => (
    <div key={user.id} className="card" style={{ marginBottom: '12px', padding: '16px', border: selectedUsers.has(user.id) ? '2px solid var(--primary)' : '1px solid var(--border-light)', background: selectedUsers.has(user.id) ? 'var(--bg-muted)' : 'var(--bg-card)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <input type="checkbox" checked={selectedUsers.has(user.id)} onChange={() => handleSelectUser(user.id)} style={{ cursor: 'pointer', width: '20px', height: '20px' }} />
          <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-full)', background: user.enabled ? 'var(--gradient-primary)' : 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600, fontSize: '0.875rem', flexShrink: 0 }}>{user.firstName?.[0] || ''}{user.lastName?.[0] || ''}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.firstName} {user.lastName}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--primary)', fontWeight: 500 }}>{user.username}</div>
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <button className="btn btn-ghost btn-sm" style={{ padding: '8px' }} onClick={(e) => { e.stopPropagation(); setActiveActionMenu(activeActionMenu === user.id ? null : user.id); }}><HiOutlineDotsVertical style={{ fontSize: '1.25rem' }} /></button>
          {activeActionMenu === user.id && (
            <div style={{ position: 'absolute', right: 0, top: '100%', background: 'var(--bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)', zIndex: 100, minWidth: '160px', padding: '8px 0' }} onClick={(e) => e.stopPropagation()}>
              <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }} onClick={() => handleEditUser(user)}><HiOutlinePencil style={{ color: 'var(--accent-cyan)' }} /> Editar</button>
              <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }} onClick={() => { handleBlock(user); setActiveActionMenu(null); }}>{user.enabled ? <HiOutlineLockClosed style={{ color: 'var(--accent-pink)' }} /> : <HiOutlineLockOpen style={{ color: 'var(--secondary)' }} />}{user.enabled ? 'Bloquear' : 'Desbloquear'}</button>
              <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-primary)' }} onClick={() => handleOpenPasswordModal(user.id)}><HiOutlineKey style={{ color: 'var(--primary)' }} /> Resetear contraseña</button>
              <button style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--accent-pink)' }} onClick={() => { handleDelete(user); setActiveActionMenu(null); }}><HiOutlineTrash /> Eliminar</button>
            </div>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
        <div><strong>Email:</strong> {user.email}</div>
        <div><strong>Teléfono:</strong> {user.phone || '-'}</div>
        <div><strong>Puesto:</strong> {user.userRole || '-'}</div>
        <div><strong>Score:</strong> <span style={{ color: 'var(--accent-orange)', fontWeight: 600 }}>{user.score}</span></div>
        <div><strong>Asesor:</strong> {user.adviser || '-'}</div>
        <div><strong>Registro:</strong> {user.registrationDate ? new Date(user.registrationDate).toLocaleDateString('es-MX') : '-'}</div>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
        {getEstadoBadge(user.enabled)}
        {getSolicitudBadge(user.requestStatus)}
        {user.groups?.filter(g => g !== ADMIN_GROUP).slice(0, 2).map((grupo, i) => <span key={i} style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: '0.6875rem', fontWeight: 500, background: 'var(--bg-body)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' }}>{grupo}</span>)}
        {user.groups && user.groups.filter(g => g !== ADMIN_GROUP).length > 2 && <span style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', fontSize: '0.6875rem', fontWeight: 600, background: 'var(--primary)', color: 'white' }}>+{user.groups.filter(g => g !== ADMIN_GROUP).length - 2}</span>}
      </div>
    </div>
  );

  return (
    <>
      <div className="card card-glow" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <div className="card-header" style={{ background: 'transparent', border: 'none', padding: isMobile ? '0.75rem' : '0.875rem 1.25rem', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <HiOutlineSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '1rem' }} />
                <input type="text" placeholder="Buscar usuarios..." value={filters.search} onChange={handleSearch} className="form-control" style={{ paddingLeft: '34px', fontSize: '0.8125rem', height: '36px' }} />
              </div>
              <button className={`btn ${hasActiveFilters ? 'btn-primary' : 'btn-outline'} btn-sm`} onClick={() => setShowFilters(!showFilters)} title="Filtros" style={{ padding: '8px', height: '36px', position: 'relative', flexShrink: 0 }}>
                <HiOutlineFilter />
                {hasActiveFilters && <span style={{ position: 'absolute', top: '-3px', right: '-3px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--accent-pink)' }} />}
              </button>
              {!isMobile && <button className="btn btn-outline btn-sm" onClick={() => void handleExport()} disabled={exporting} title={exporting ? 'Exportando...' : 'Exportar'} style={{ padding: '8px', height: '36px', flexShrink: 0 }}><HiOutlineDownload /></button>}
              <button className="btn btn-primary btn-sm" onClick={handleNewUser} style={{ padding: '8px 12px', height: '36px', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <HiOutlinePlus />
                {!isMobile && <span style={{ fontSize: '0.8125rem' }}>Nuevo</span>}
              </button>
            </div>
            {showFilters && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr auto', gap: '0.5rem', padding: '0.625rem', background: 'var(--bg-body)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
                <select value={filters.userRole} onChange={handlePuestoFilter} className="form-control" style={{ fontSize: '0.8125rem' }}>
                  <option value="">Todos los puestos</option>
                  {PUESTOS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={filters.enabled === undefined ? '' : filters.enabled ? 'true' : 'false'} onChange={handleEstadoFilter} className="form-control" style={{ fontSize: '0.8125rem' }}>
                  <option value="">Todos los estados</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
                {hasActiveFilters && <button className="btn btn-ghost btn-sm" onClick={handleClearFilters} style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}><HiOutlineX /> Limpiar</button>}
              </div>
            )}
            {selectedUsers.size > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{selectedUsers.size} seleccionados</span>
                <div style={{ display: 'flex', gap: '0.375rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
                  <button className="btn btn-sm" onClick={() => void handleBulkBlock()} disabled={bulkActionLoading} style={{ background: 'var(--accent-orange)', color: 'white', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}><HiOutlineLockClosed /> Bloquear</button>
                  <button className="btn btn-sm" onClick={() => void handleBulkUnblock()} disabled={bulkActionLoading} style={{ background: 'var(--secondary)', color: 'white', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}><HiOutlineLockOpen /> Desbloquear</button>
                  <button className="btn btn-sm" onClick={handleBulkDelete} disabled={bulkActionLoading} style={{ background: 'var(--accent-pink)', color: 'white', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}><HiOutlineTrash /> Eliminar</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelectedUsers(new Set())} style={{ padding: '5px' }}><HiOutlineX /></button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card-body" style={{ padding: 0, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner" /></div>
          ) : users.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No se encontraron usuarios</div>
          ) : isMobile ? (
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', position: 'sticky', top: 0, background: 'var(--bg-card)', padding: '6px 0', zIndex: 10 }}>
                <input type="checkbox" checked={users.length > 0 && selectedUsers.size === users.length} onChange={handleSelectAll} style={{ cursor: 'pointer', width: '18px', height: '18px' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Seleccionar todos</span>
              </div>
              {users.map(renderMobileCard)}
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '1300px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-body)' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', width: '42px', position: 'sticky', top: 0, left: 0, background: 'var(--bg-body)', zIndex: 30, borderBottom: '2px solid var(--border-color)' }}>
                      <input type="checkbox" checked={users.length > 0 && selectedUsers.size === users.length} onChange={handleSelectAll} style={{ cursor: 'pointer' }} />
                    </th>
                    {['ID Empleado', 'Nombre', 'Apellido', 'Fecha Reg.', 'Email', 'Solicitud', 'Puesto', 'Teléfono', 'Score', 'Grupos', 'Asesor', 'Estado'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--bg-body)', zIndex: 20, borderBottom: '2px solid var(--border-color)' }}>{h}</th>
                    ))}
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', position: 'sticky', top: 0, right: 0, background: 'var(--bg-body)', zIndex: 30, borderBottom: '2px solid var(--border-color)', boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.15)', minWidth: '120px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id} style={{ background: selectedUsers.has(user.id) ? 'var(--bg-muted)' : index % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-body)' }}
                      onMouseEnter={(e) => { if (!selectedUsers.has(user.id)) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { if (!selectedUsers.has(user.id)) e.currentTarget.style.background = index % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-body)'; }}>
                      <td style={{ padding: '6px 12px', position: 'sticky', left: 0, background: '#fff', zIndex: 10, borderBottom: '1px solid var(--border-light)' }}><input type="checkbox" checked={selectedUsers.has(user.id)} onChange={() => handleSelectUser(user.id)} style={{ cursor: 'pointer' }} /></td>
                      <td style={{ padding: '6px 12px', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--primary)', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.username}</td>
                      <td style={{ padding: '6px 12px', fontSize: '0.8125rem', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.firstName}</td>
                      <td style={{ padding: '6px 12px', fontSize: '0.8125rem', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.lastName}</td>
                      <td style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.registrationDate ? new Date(user.registrationDate).toLocaleDateString('es-MX') : '-'}</td>
                      <td style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-light)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</td>
                      <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-light)' }}>{getSolicitudBadge(user.requestStatus)}</td>
                      <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-light)' }}><span style={{ padding: '3px 8px', borderRadius: 'var(--radius-full)', fontSize: '0.6875rem', fontWeight: 500, background: 'rgba(101, 45, 134, 0.1)', color: 'var(--primary)', whiteSpace: 'nowrap' }}>{user.userRole || '-'}</span></td>
                      <td style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.phone || '-'}</td>
                      <td style={{ padding: '6px 12px', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--accent-orange)', borderBottom: '1px solid var(--border-light)' }}>{user.score}</td>
                      <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-light)' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', maxWidth: '140px' }}>
                          {user.groups?.filter(g => g !== ADMIN_GROUP).slice(0, 2).map((g, i) => (<span key={i} style={{ padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontSize: '0.625rem', fontWeight: 500, background: 'var(--bg-body)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', whiteSpace: 'nowrap' }}>{g}</span>))}
                          {user.groups && user.groups.filter(g => g !== ADMIN_GROUP).length > 2 && (<span style={{ padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontSize: '0.625rem', fontWeight: 600, background: 'var(--primary)', color: 'white' }}>+{user.groups.filter(g => g !== ADMIN_GROUP).length - 2}</span>)}
                        </div>
                      </td>
                      <td style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-light)', whiteSpace: 'nowrap' }}>{user.adviser || '-'}</td>
                      <td style={{ padding: '6px 12px', borderBottom: '1px solid var(--border-light)' }}>{getEstadoBadge(user.enabled)}</td>
                      <td style={{ padding: '6px 12px', position: 'sticky', right: 0, background: '#fff', zIndex: 10, borderBottom: '1px solid var(--border-light)', boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.15)' }}>
                        <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px', color: 'var(--accent-cyan)' }} title="Editar" onClick={() => handleEditUser(user)}><HiOutlinePencil /></button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px', color: user.enabled ? 'var(--accent-pink)' : 'var(--secondary)' }} title={user.enabled ? 'Bloquear' : 'Desbloquear'} onClick={() => handleBlock(user)}>{user.enabled ? <HiOutlineLockClosed /> : <HiOutlineLockOpen />}</button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px', color: 'var(--primary)' }} title="Resetear contraseña" onClick={() => handleOpenPasswordModal(user.id)}><HiOutlineKey /></button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '5px', color: 'var(--accent-pink)' }} title="Eliminar" onClick={() => handleDelete(user)}><HiOutlineTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card-footer" style={{ background: 'var(--bg-card)', border: 'none', padding: isMobile ? '0.75rem' : '0.625rem 1.25rem', borderTop: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', flexShrink: 0 }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{users.length} de {total} usuarios</div>
          <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '5px 9px' }}><HiOutlineChevronLeft /></button>
            {!isMobile && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum = i + 1;
              if (totalPages > 5) { if (page > 3) pageNum = page - 2 + i; if (page > totalPages - 2) pageNum = totalPages - 4 + i; }
              return <button key={pageNum} className={`btn btn-sm ${page === pageNum ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setPage(pageNum)} style={{ padding: '5px 10px', minWidth: '32px', fontSize: '0.8125rem' }}>{pageNum}</button>;
            })}
            {isMobile && <span style={{ fontSize: '0.8125rem', padding: '0 8px' }}>{page} / {totalPages}</span>}
            <button className="btn btn-ghost btn-sm" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)} style={{ padding: '5px 9px' }}><HiOutlineChevronRight /></button>
          </div>
        </div>
      </div>

      {showUserModal && <UserFormModal user={editingUser} onClose={() => { setShowUserModal(false); setEditingUser(null); }} onSuccess={handleUserModalSuccess} onError={handleUserModalError} />}
      {showPasswordModal && selectedUserId && <PasswordResetModal userId={selectedUserId} onClose={() => { setShowPasswordModal(false); setSelectedUserId(null); }} onSuccess={handlePasswordModalSuccess} onError={handlePasswordModalError} />}
      {exporting && <ExportLoadingModal fileName="usuarios_export.csv" />}
      <Modal {...modalState} onClose={closeModal} />
      <Toast {...toastState} onClose={closeToast} />
    </>
  );
}


function PasswordResetModal({ userId, onClose, onSuccess, onError }: { userId: string; onClose: () => void; onSuccess: () => void; onError: (msg: string) => void }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [temporary, setTemporary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const isMobile = useIsMobile();
  const { allPassed } = usePasswordStrength(password);

  const passwordsMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const canSubmit = allPassed && passwordsMatch && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allPassed) {
      onError('La contraseña no cumple con todos los requisitos de seguridad');
      return;
    }
    if (password !== confirmPassword) {
      onError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await resetUserPassword(userId, password, temporary);
      onSuccess();
    } catch {
      onError('Error al resetear la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
      <div className="card" style={{ width: isMobile ? '100%' : '440px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', overflow: 'auto', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-light)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HiOutlineKey style={{ color: 'var(--primary)' }} />
            Resetear Contraseña
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><HiOutlineX /></button>
        </div>
        <form onSubmit={e => { void handleSubmit(e); }}>
          <div className="card-body" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Nueva Contraseña</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="form-control"
                  required
                  placeholder="Ingresa la nueva contraseña"
                  style={{
                    paddingRight: '40px',
                    borderColor: password.length > 0 ? (allPassed ? '#22c55e' : 'var(--border-color)') : undefined,
                  }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  {showPassword ? <HiOutlineEyeOff style={{ fontSize: '1.125rem' }} /> : <HiOutlineEye style={{ fontSize: '1.125rem' }} />}
                </button>
              </div>

             
              <PasswordStrengthIndicator password={password} />
            </div>

           
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Confirmar Contraseña</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="form-control"
                  required
                  placeholder="Confirma la contraseña"
                  style={{
                    paddingRight: '40px',
                    borderColor: passwordsMismatch ? '#ef4444' : passwordsMatch ? '#22c55e' : undefined,
                  }}
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                  {showConfirmPassword ? <HiOutlineEyeOff style={{ fontSize: '1.125rem' }} /> : <HiOutlineEye style={{ fontSize: '1.125rem' }} />}
                </button>
              </div>

              
              {passwordsMismatch && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '0.75rem', color: '#ef4444' }}>
                  <HiOutlineX style={{ fontSize: '0.875rem' }} />
                  Las contraseñas no coinciden
                </div>
              )}
              {passwordsMatch && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '0.75rem', color: '#22c55e' }}>
                  <HiOutlineCheck style={{ fontSize: '0.875rem' }} />
                  Las contraseñas coinciden
                </div>
              )}
            </div>
          </div>
          <div className="card-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!canSubmit}
              style={{
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserFormModal({ user, onClose, onSuccess, onError }: { user: UserRecord | null; onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void }) {
  const [formData, setFormData] = useState<Partial<UserRecord>>(user || { username: '', email: '', firstName: '', lastName: '', userRole: '', phone: '', score: 0, adviser: '', requestStatus: 'Pendiente', groups: [], enabled: true });
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const isMobile = useIsMobile();

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const response = await getGroups(1, 100);
        const filtered = response.data.filter(g => !PROTECTED_GROUPS.includes(g.nombre) || g.nombre === 'Appy-User');
        setGroups(filtered);

        if (user?.groups?.length) {
          const currentGroupName = user.groups.find(g => g !== ADMIN_GROUP);
          if (currentGroupName) {
            const match = filtered.find(g => g.nombre === currentGroupName);
            if (match) setSelectedGroupId(match.id);
          }
        }
      } catch { }
    };
    void loadGroups();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (user) {
        const updatePayload = { ...formData, groupId: selectedGroupId || undefined };
        delete (updatePayload as Record<string, unknown>).groups;
        await updateUser(user.id, updatePayload);
        onSuccess('Usuario actualizado exitosamente');
      } else {
        const selectedGroup = groups.find(g => g.id === selectedGroupId);
        const createPayload = { ...formData, groupName: selectedGroup?.nombre || '' };
        delete (createPayload as Record<string, unknown>).groups;
        await createUser(createPayload as Omit<UserRecord, 'id'> & { groupName?: string });
        onSuccess('Usuario creado exitosamente');
      }
    } catch {
      onError(user ? 'Error al actualizar usuario' : 'Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
      <div className="card" style={{ width: isMobile ? '100%' : '600px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', overflow: 'auto', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-light)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem' }}>{user ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><HiOutlineX /></button>
        </div>
        <form onSubmit={e => { void handleSubmit(e); }}>
          <div className="card-body" style={{ padding: '1.5rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>ID Empleado *</label>
              <input type="text" value={formData.username || ''} onChange={e => setFormData({ ...formData, username: e.target.value })} className="form-control" required disabled={!!user} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Email *</label>
              <input type="email" value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} className="form-control" required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Nombre *</label>
              <input type="text" value={formData.firstName || ''} onChange={e => setFormData({ ...formData, firstName: e.target.value })} className="form-control" required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Apellido *</label>
              <input type="text" value={formData.lastName || ''} onChange={e => setFormData({ ...formData, lastName: e.target.value })} className="form-control" required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Puesto</label>
              <select value={formData.userRole || ''} onChange={e => setFormData({ ...formData, userRole: e.target.value })} className="form-control">
                <option value="">Seleccionar...</option>
                {PUESTOS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Teléfono</label>
              <input type="tel" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="form-control" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Score</label>
              <input type="number" value={formData.score || 0} onChange={e => setFormData({ ...formData, score: parseInt(e.target.value) || 0 })} className="form-control" min={0} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Asesor</label>
              <input type="text" value={formData.adviser || ''} onChange={e => setFormData({ ...formData, adviser: e.target.value })} className="form-control" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Estado Solicitud</label>
              <select value={formData.requestStatus || 'Pendiente'} onChange={e => setFormData({ ...formData, requestStatus: e.target.value as 'Aprobada' | 'Pendiente' | 'Rechazada' })} className="form-control">
                {SOLICITUD_ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Grupo</label>
              <select value={selectedGroupId} disabled={user?true:false} onChange={e => setSelectedGroupId(e.target.value)} className="form-control">
                <option value="">Sin grupo</option>
                {groups.filter(g => g.nombre !== ADMIN_GROUP).map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Fecha de Registro</label>
              <input type="date" value={formData.registrationDate || ''} onChange={e => setFormData({ ...formData, registrationDate: e.target.value })} className="form-control" />
            </div>
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="checkbox" id="enabled" checked={formData.enabled} onChange={e => setFormData({ ...formData, enabled: e.target.checked })} />
                <label htmlFor="enabled" style={{ fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}>Usuario Activo</label>
              </div>
            )}
          </div>
          <div className="card-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GroupsTab(): JSX.Element {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<UserGroupFilters>({ search: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);

  const isMobile = useIsMobile();
  const { modalState, showError, showDelete, closeModal, setLoading: setModalLoading } = useModal();
  const { toastState, showSuccess: toastSuccess, showError: toastError, closeToast } = useToast();

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGroups(page, 10, filters);
      setGroups(response.data.filter(g => g.nombre !== ADMIN_GROUP));
      setTotalPages(response.totalPages);
    } catch { toastError('Error al cargar grupos'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { void fetchGroups(); }, [fetchGroups]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(1); };
  const handleNewGroup = () => { setEditingGroup(null); setShowGroupModal(true); };
  const handleEditGroup = (group: UserGroup) => { setEditingGroup(group); setShowGroupModal(true); };
  const handleViewMembers = (group: UserGroup) => { setSelectedGroup(group); setShowMembersModal(true); };

  const handleDeleteGroup = (group: UserGroup) => {
    if (PROTECTED_GROUPS.includes(group.nombre)) { showError({ title: 'Error', message: 'No se puede eliminar un grupo protegido del sistema' }); return; }
    showDelete({
      title: '¿Eliminar grupo?', message: `¿Estás seguro de eliminar el grupo "${group.nombre}"?`, confirmText: 'Eliminar',
      onConfirm: async () => {
        setModalLoading(true);
        try { await deleteGroup(group.id); closeModal(); toastSuccess({ title: '¡Eliminado!', message: 'Grupo eliminado' }); void fetchGroups(); }
        catch { closeModal(); showError({ title: 'Error', message: 'No se pudo eliminar el grupo' }); }
      }
    });
  };

  const handleGroupModalSuccess = (message: string) => { setShowGroupModal(false); setEditingGroup(null); toastSuccess({ title: '¡Éxito!', message }); void fetchGroups(); };
  const handleGroupModalError = (message: string) => { showError({ title: 'Error', message }); };
  const isProtected = (group: UserGroup) => PROTECTED_GROUPS.includes(group.nombre);

  return (
    <>
      <div className="card card-glow" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <div className="card-header" style={{ background: 'transparent', border: 'none', padding: isMobile ? '0.75rem' : '0.875rem 1.25rem', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <HiOutlineSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '1rem' }} />
              <input type="text" placeholder="Buscar grupos..." value={filters.search} onChange={handleSearch} className="form-control" style={{ paddingLeft: '34px', fontSize: '0.8125rem', height: '36px' }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleNewGroup} style={{ padding: '8px 12px', height: '36px', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <HiOutlinePlus />
              {!isMobile && <span style={{ fontSize: '0.8125rem' }}>Nuevo</span>}
            </button>
          </div>
        </div>

        <div className="card-body" style={{ padding: 0, flex: 1, minHeight: 0, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : groups.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>No se encontraron grupos</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', padding: '0.75rem' }}>
              {groups.map(group => (
                <div key={group.id} className="card" style={{ border: '1px solid var(--border-light)', borderLeft: `3px solid ${group.color}` }}>
                  <div className="card-body" style={{ padding: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.625rem' }}>
                      <div style={{ minWidth: 0, flex: 1, marginRight: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                          {group.nombre}
                        </h4>
                        {group.descripcion && <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{group.descripcion}</p>}
                      </div>
                      {isProtected(group) && <span style={{ padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontSize: '0.625rem', fontWeight: 600, background: 'rgba(101, 45, 134, 0.1)', color: 'var(--primary)', flexShrink: 0 }}>Sistema</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.375rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleViewMembers(group)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '5px', fontSize: '0.8125rem' }}><HiOutlineEye /> Ver miembros</button>
                      {!isProtected(group) && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleEditGroup(group)} style={{ padding: '5px', color: 'var(--accent-cyan)', flexShrink: 0 }}><HiOutlinePencil /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteGroup(group)} style={{ padding: '5px', color: 'var(--accent-pink)', flexShrink: 0 }}><HiOutlineTrash /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-footer" style={{ background: 'var(--bg-card)', border: 'none', padding: isMobile ? '0.75rem' : '0.625rem 1.25rem', borderTop: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{groups.length} grupos</div>
          <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '5px 9px' }}><HiOutlineChevronLeft /></button>
            <span style={{ fontSize: '0.8125rem', padding: '0 8px' }}>{page} / {totalPages || 1}</span>
            <button className="btn btn-ghost btn-sm" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)} style={{ padding: '5px 9px' }}><HiOutlineChevronRight /></button>
          </div>
        </div>
      </div>

      {showGroupModal && <GroupFormModal group={editingGroup} onClose={() => { setShowGroupModal(false); setEditingGroup(null); }} onSuccess={handleGroupModalSuccess} onError={handleGroupModalError} />}
      {showMembersModal && selectedGroup && <GroupMembersModal group={selectedGroup} onClose={() => { setShowMembersModal(false); setSelectedGroup(null); }} onUpdate={() => void fetchGroups()} />}
      <Modal {...modalState} onClose={closeModal} />
      <Toast {...toastState} onClose={closeToast} />
    </>
  );
}

function GroupFormModal({ group, onClose, onSuccess, onError }: { group: UserGroup | null; onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void }) {
  const [formData, setFormData] = useState({ name: group?.nombre || '', description: group?.descripcion || '', color: group?.color || GROUP_COLORS[0] });
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name === ADMIN_GROUP) { onError('No se puede crear un grupo con ese nombre'); return; }
    setLoading(true);
    try {
      if (group) { await updateGroup(group.id, formData); onSuccess('Grupo actualizado exitosamente'); }
      else { await createGroup(formData); onSuccess('Grupo creado exitosamente'); }
    } catch { onError(group ? 'Error al actualizar grupo' : 'Error al crear grupo'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
      <div className="card" style={{ width: isMobile ? '100%' : '450px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh', overflow: 'auto', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-light)' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem' }}>{group ? 'Editar Grupo' : 'Nuevo Grupo'}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><HiOutlineX /></button>
        </div>
        <form onSubmit={e => { void handleSubmit(e); }}>
          <div className="card-body" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Nombre *</label>
              <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="form-control" required />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Descripción</label>
              <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="form-control" rows={3} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Color</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {GROUP_COLORS.map(color => (<button key={color} type="button" onClick={() => setFormData({ ...formData, color })} style={{ width: '32px', height: '32px', borderRadius: 'var(--radius-sm)', background: color, border: formData.color === color ? '3px solid var(--text-primary)' : '2px solid transparent', cursor: 'pointer', transition: 'all 0.2s' }} />))}
              </div>
            </div>
          </div>
          <div className="card-footer" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-light)' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GroupMembersModal({ group, onClose, onUpdate }: { group: UserGroup; onClose: () => void; onUpdate: () => void }) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<GroupMember[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = useIsMobile();
  const { toastState, showSuccess: toastSuccess, showError: toastError, closeToast } = useToast();

  useEffect(() => {
    const loadMembers = async () => {
      try { const response = await getGroupMembers(group.id); setMembers(response.data); }
      catch { toastError('Error al cargar miembros'); }
      finally { setLoading(false); }
    };
    void loadMembers();
  }, [group.id]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (userSearch.trim().length < 2) { setSearchResults([]); setShowDropdown(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await getUsers(1, 10, { search: userSearch });
        const filtered = response.data.filter(u => !members.some(m => m.id === u.id));
        setSearchResults(filtered.map(u => ({ id: u.id, username: u.username, email: u.email, firstName: u.firstName, lastName: u.lastName, enabled: u.enabled })));
        setShowDropdown(true);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [userSearch, members]);

  const handleRemoveMember = async (userId: string) => {
    if (group.nombre === ADMIN_GROUP) { toastError('No se pueden modificar miembros de este grupo'); return; }
    setRemoving(userId);
    try { await removeUserFromGroup(group.id, userId); setMembers(prev => prev.filter(m => m.id !== userId)); toastSuccess({ title: '¡Éxito!', message: 'Usuario removido del grupo' }); onUpdate(); }
    catch { toastError('Error al remover usuario'); }
    finally { setRemoving(null); }
  };

  const handleAddMember = async (user: GroupMember) => {
    if (group.nombre === ADMIN_GROUP) { toastError('No se pueden modificar miembros de este grupo'); return; }
    setAdding(user.id);
    try { await addUserToGroup(group.id, user.id); setMembers(prev => [...prev, user]); setUserSearch(''); setSearchResults([]); setShowDropdown(false); toastSuccess({ title: '¡Éxito!', message: 'Usuario agregado al grupo' }); onUpdate(); }
    catch { toastError('Error al agregar usuario'); }
    finally { setAdding(null); }
  };

  const isAdminGroup = group.nombre === ADMIN_GROUP;

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : '1rem' }}>
      <div className="card" style={{ width: isMobile ? '100%' : '500px', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '85vh', display: 'flex', flexDirection: 'column', borderRadius: isMobile ? 0 : 'var(--radius-lg)' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.125rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: group.color }} />
              {group.nombre}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{members.length} miembros</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><HiOutlineX /></button>
        </div>

        

        <div className="card-body" style={{ padding: 0, flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : members.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No hay miembros en este grupo</div>
          ) : (
            <div style={{ padding: '0.5rem 0' }}>
              {members.map(member => (
                <div key={member.id} style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--border-light)', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-full)', background: member.enabled ? 'var(--gradient-primary)' : 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>{member.firstName?.[0] || ''}{member.lastName?.[0] || ''}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '6px' }}>{member.firstName} {member.lastName}<span style={{ padding: '1px 6px', borderRadius: 'var(--radius-sm)', fontSize: '0.625rem', fontWeight: 600, background: 'rgba(101, 45, 134, 0.1)', color: 'var(--primary)' }}>{member.username}</span></div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{member.email}</div>
                  </div>
                  {!member.enabled && <span style={{ padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: '0.6875rem', background: 'rgba(156, 163, 175, 0.12)', color: 'var(--text-muted)' }}>Inactivo</span>}
                  {!isAdminGroup && (
                    <button className="btn btn-ghost btn-sm" onClick={() => void handleRemoveMember(member.id)} disabled={removing === member.id} style={{ padding: '6px', color: 'var(--accent-pink)' }}>
                      {removing === member.id ? <div className="spinner" style={{ width: '16px', height: '16px' }} /> : <HiOutlineUserRemove />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border-light)', flexShrink: 0 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>Cerrar</button>
        </div>
      </div>
      <Toast {...toastState} onClose={closeToast} />
    </div>
  );
}

export default UsersPage;